import React, { useMemo } from 'react'
import { useTranslate } from '@ur-solutions/react-hooks'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import { FAIcon } from '@/components_deprecated/icons'
import type { OvertimeLimitForm } from '../SalarySettingsFormView'
import { DurationInput, decimalToHm } from './duration-input'

interface Props {
  dailyWorkLimit: number
  onDailyWorkLimitChange: (value: number) => void
  dayOvertimeSalaryCode: string | null
  onDayOvertimeSalaryCodeChange: (value: string | null) => void
  overtimeLimits: OvertimeLimitForm[]
  onOvertimeLimitsChange: (value: OvertimeLimitForm[]) => void
  /**
   * When inline overtime calculation is disabled tiers cannot be expressed at
   * the calc layer, so non-base rows + the add button are disabled. Base row
   * stays editable.
   */
  tiersDisabled?: boolean
}

interface RowMeta {
  threshold: number
  range: string
  salaryCode: string | null
  isBase: boolean
  index: number | null
  error: string | null
}

const MAX_BASE_HOURS = 24
const MAX_TIER_HOURS = 48

const formatHm = (decimalHours: number): string => {
  const { h, m } = decimalToHm(decimalHours)
  return m === 0 ? `${h} h` : `${h} h ${m} m`
}

const GRID_SPACING = 'md:grid-cols-[100px_115px_minmax(0,1fr)_28px]'

const HEADER_GRID = `hidden md:grid ${GRID_SPACING} gap-3 items-center`

export const DailyOvertimeEditor: React.FC<Props> = ({
  dailyWorkLimit,
  onDailyWorkLimitChange,
  dayOvertimeSalaryCode,
  onDayOvertimeSalaryCodeChange,
  overtimeLimits,
  onOvertimeLimitsChange,
  tiersDisabled = false,
}) => {
  const t = useTranslate({
    title: 'settings.daily-overtime-title',
    after: 'settings.daily-overtime-after',
    range: 'settings.daily-overtime-range',
    salaryCode: 'settings.daily-overtime-salary-code',
    baseTooltip: 'settings.daily-overtime-base-tooltip',
    removeTier: 'settings.daily-overtime-remove-tier',
    addTier: 'settings.daily-overtime-add-tier',
    errorDuplicate: 'settings.daily-overtime-error-duplicate',
    errorBelowBase: 'settings.daily-overtime-error-below-base',
    errorMissingCode: 'settings.daily-overtime-error-missing-code',
    inlineActiveNotice: 'settings.daily-overtime-inline-active-notice',
  })

  const sortedLimits = useMemo(
    () =>
      overtimeLimits
        .map((limit, originalIndex) => ({ ...limit, originalIndex }))
        .sort((a, b) => a.threshold - b.threshold),
    [overtimeLimits]
  )

  const thresholdCounts = useMemo(() => {
    const counts: Record<number, number> = {}
    for (const l of overtimeLimits) {
      counts[l.threshold] = (counts[l.threshold] ?? 0) + 1
    }
    return counts
  }, [overtimeLimits])

  const rangeByIndex = useMemo(() => {
    const map: Record<number, string> = {}
    sortedLimits.forEach((limit, i) => {
      const next = sortedLimits[i + 1]?.threshold
      map[limit.originalIndex] =
        next !== undefined
          ? `${formatHm(limit.threshold)} → ${formatHm(next)}`
          : `${formatHm(limit.threshold)}+`
    })
    return map
  }, [sortedLimits])

  const baseRange = useMemo(() => {
    const next = sortedLimits.find(l => l.threshold > dailyWorkLimit)?.threshold
    return next !== undefined
      ? `${formatHm(dailyWorkLimit)} → ${formatHm(next)}`
      : `${formatHm(dailyWorkLimit)}+`
  }, [dailyWorkLimit, sortedLimits])

  const rows: RowMeta[] = useMemo(() => {
    const all: RowMeta[] = []
    all.push({
      threshold: dailyWorkLimit,
      range: baseRange,
      salaryCode: dayOvertimeSalaryCode,
      isBase: true,
      index: null,
      error: null,
    })
    overtimeLimits.forEach((limit, originalIndex) => {
      const isDuplicate = (thresholdCounts[limit.threshold] ?? 0) > 1
      const isBelowBase = limit.threshold <= dailyWorkLimit
      const missingCode = !limit.salaryCode
      let error: string | null = null
      if (isDuplicate) error = t.errorDuplicate
      else if (isBelowBase) error = t.errorBelowBase
      else if (missingCode) error = t.errorMissingCode
      all.push({
        threshold: limit.threshold,
        range: rangeByIndex[originalIndex] ?? `${formatHm(limit.threshold)}+`,
        salaryCode: limit.salaryCode,
        isBase: false,
        index: originalIndex,
        error,
      })
    })
    return all
  }, [
    dailyWorkLimit,
    dayOvertimeSalaryCode,
    baseRange,
    overtimeLimits,
    rangeByIndex,
    thresholdCounts,
    t.errorDuplicate,
    t.errorBelowBase,
    t.errorMissingCode,
  ])

  const updateLimit = (
    originalIndex: number,
    patch: Partial<OvertimeLimitForm>
  ) => {
    const next = overtimeLimits.map((l, i) =>
      i === originalIndex ? { ...l, ...patch } : l
    )
    onOvertimeLimitsChange(next)
  }

  const removeLimit = (originalIndex: number) => {
    onOvertimeLimitsChange(overtimeLimits.filter((_, i) => i !== originalIndex))
  }

  const canAddTier = useMemo(() => {
    const last = sortedLimits[sortedLimits.length - 1]
    const baseline = last?.threshold ?? dailyWorkLimit
    return baseline < MAX_TIER_HOURS
  }, [sortedLimits, dailyWorkLimit])

  const addTier = () => {
    if (!canAddTier || tiersDisabled) return
    const used = new Set(overtimeLimits.map(l => l.threshold))
    const last = sortedLimits[sortedLimits.length - 1]
    const baseline = last?.threshold ?? dailyWorkLimit
    let candidate = Math.min(baseline + 1, MAX_TIER_HOURS)
    while (used.has(candidate) && candidate < MAX_TIER_HOURS) candidate += 1
    if (used.has(candidate)) return
    onOvertimeLimitsChange([
      ...overtimeLimits,
      { threshold: candidate, salaryCode: null },
    ])
  }

  return (
    <div className="flex flex-col">
      <label className="text-[0.9rem] font-[600] mb-[6px] ml-[6px] text-[#9696A2]">
        {t.title}
      </label>
      <div className="flex flex-col rounded-md border border-[#e2e8f0] bg-white">
        {tiersDisabled && (
          <div className="flex items-start gap-2 px-4 py-3 border-b border-[#fde68a] bg-[#fffbeb] text-[12px] text-[#92400e] rounded-t-md">
            <span className="mt-[2px] shrink-0">
              <FAIcon icon="circle-info" $size="0.9rem" />
            </span>
            <span className="leading-snug">{t.inlineActiveNotice}</span>
          </div>
        )}
        <div
          className={`${HEADER_GRID} px-4 py-2 bg-quaternary text-[10px] font-semibold tracking-wider text-tertiary`}
        >
          <div>{t.after.toUpperCase()}</div>
          <div>{t.range.toUpperCase()}</div>
          <div>{t.salaryCode.toUpperCase()}</div>
          <div />
        </div>
        {rows.map(row => {
          const handleThresholdChange = (value: number) => {
            if (row.isBase) {
              onDailyWorkLimitChange(value)
            } else if (row.index !== null) {
              updateLimit(row.index, { threshold: value })
            }
          }
          const handleSalaryCodeChange = (value: string | null) => {
            if (row.isBase) {
              onDayOvertimeSalaryCodeChange(value)
            } else if (row.index !== null) {
              updateLimit(row.index, { salaryCode: value })
            }
          }
          const rowFieldsDisabled = tiersDisabled && !row.isBase
          const ActionButton = row.isBase ? (
            <button
              type="button"
              disabled
              title={t.baseTooltip}
              className="w-7 h-7 flex items-center justify-center text-tertiary cursor-default bg-transparent border-0"
            >
              <FAIcon icon="lock" />
            </button>
          ) : (
            <button
              type="button"
              title={t.removeTier}
              onClick={() => row.index !== null && removeLimit(row.index!)}
              className="w-7 h-7 flex items-center justify-center text-tertiary cursor-pointer bg-transparent border-0 rounded hover:bg-quaternary hover:text-primary"
            >
              <FAIcon icon="trash" />
            </button>
          )
          return (
            <React.Fragment key={row.isBase ? 'base' : `tier-${row.index}`}>
              {/* Mobile: stacked. Desktop: 4-col grid. */}
              <div
                className={`px-4 py-3 border-t border-[#e2e8f0] ${
                  row.isBase ? 'bg-quaternary/40' : ''
                } md:hidden`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <DurationInput
                    value={row.threshold}
                    minHours={row.isBase ? 0 : dailyWorkLimit + 1 / 60}
                    maxHours={row.isBase ? MAX_BASE_HOURS : MAX_TIER_HOURS}
                    onChange={handleThresholdChange}
                    disabled={rowFieldsDisabled}
                  />
                  <div className="flex-1 text-xs text-tertiary">
                    {row.range}
                  </div>
                  {ActionButton}
                </div>
                <div
                  className={
                    rowFieldsDisabled ? 'pointer-events-none opacity-60' : ''
                  }
                >
                  <SalaryCodeSelect
                    fullWidth
                    nullable
                    value={row.salaryCode}
                    onChange={handleSalaryCodeChange}
                  />
                </div>
              </div>
              <div
                className={`hidden md:grid ${GRID_SPACING} gap-3 items-center px-4 py-2 border-t border-[#e2e8f0] ${
                  row.isBase ? 'bg-quaternary/40' : ''
                }`}
              >
                <DurationInput
                  value={row.threshold}
                  minHours={row.isBase ? 0 : dailyWorkLimit + 1 / 60}
                  maxHours={row.isBase ? MAX_BASE_HOURS : MAX_TIER_HOURS}
                  onChange={handleThresholdChange}
                  disabled={rowFieldsDisabled}
                />
                <div className="text-xs text-tertiary truncate">
                  {row.range}
                </div>
                <div
                  className={`min-w-0 ${
                    rowFieldsDisabled ? 'pointer-events-none opacity-60' : ''
                  }`}
                >
                  <SalaryCodeSelect
                    fullWidth
                    nullable
                    value={row.salaryCode}
                    onChange={handleSalaryCodeChange}
                  />
                </div>
                {ActionButton}
              </div>
              {row.error && (
                <div className="flex items-center gap-1.5 px-4 pb-2 text-[11px] font-medium text-red-600">
                  ⚠ {row.error}
                </div>
              )}
            </React.Fragment>
          )
        })}
        <button
          type="button"
          onClick={addTier}
          disabled={!canAddTier || tiersDisabled}
          className="flex items-center justify-center gap-2 w-full py-4 border-0 border-t border-[#e2e8f0] text-[0.9rem] font-semibold text-secondary cursor-pointer bg-secondary/10 hover:bg-secondary/20 rounded-b-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-secondary/10"
        >
          <FAIcon icon="plus-circle" /> {t.addTier}
        </button>
      </div>
    </div>
  )
}
